// packages/ui/src/sidebar/ActivityBar.tsx
import { useSyncExternalStore, type ReactElement } from 'react';
import { FilePlus, FileUp, FolderOpen, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { SidebarPanelRegistry } from './SidebarPanelRegistry';
import { useSidebarStore } from './sidebarStore';
import { useMainViews } from '../mainview/useMainViews';
import type { MainViewItem } from '../mainview/MainViewRegistry';

export interface ActivityBarProps {
  onNew: () => void;
  onOpenFile: () => void;
  onOpenFolder: () => void;
}

function usePanels() {
  return useSyncExternalStore(
    SidebarPanelRegistry.subscribe,
    SidebarPanelRegistry.getAll,
    SidebarPanelRegistry.getAll,
  );
}

/**
 * The thin, always-visible left rail. Top: panel switchers (one per registered
 * sidebar panel) + the global file actions. Bottom (pinned): the sidebar
 * expand/collapse toggle.
 */
export function ActivityBar({ onNew, onOpenFile, onOpenFolder }: ActivityBarProps): ReactElement {
  const panels = usePanels();
  const mainViews = useMainViews();
  const open = useSidebarStore((s) => s.open);
  const activePanelId = useSidebarStore((s) => s.activePanelId);
  const activeMainView = useSidebarStore((s) => s.activeMainView);
  const toggleOpen = useSidebarStore((s) => s.toggleOpen);
  const setActivePanel = useSidebarStore((s) => s.setActivePanel);
  const setActiveMainView = useSidebarStore((s) => s.setActiveMainView);

  const topPanels = panels.filter((p) => (p.placement ?? 'top') === 'top');
  const bottomPanels = panels.filter((p) => p.placement === 'bottom');

  const renderPanelButton = (p: (typeof panels)[number]): ReactElement => {
    const Icon = p.icon;
    // A panel is selected only while its sidebar is the active activity-bar
    // selection: never while a main view (e.g. Git) has taken over the editor.
    const selected = !activeMainView && open && p.id === activePanelId;
    return (
      <button
        key={p.id}
        type="button"
        className="mp-activitybar__btn"
        aria-label={p.title}
        title={p.title}
        aria-pressed={selected}
        data-active={selected ? '' : undefined}
        onClick={() => (selected ? toggleOpen(false) : setActivePanel(p.id))}
      >
        <Icon size={18} aria-hidden />
      </button>
    );
  };

  // A main-view switcher opens a full-editor-area view (e.g. the Git client)
  // over the editor; clicking the active one closes it back to the editor. It
  // does NOT touch the sidebar's active panel or open state.
  const renderMainViewButton = (v: MainViewItem): ReactElement => {
    const Icon = v.icon;
    const selected = activeMainView === v.id;
    return (
      <button
        key={`mainview:${v.id}`}
        type="button"
        className="mp-activitybar__btn"
        aria-label={v.title}
        title={v.title}
        aria-pressed={selected}
        data-active={selected ? '' : undefined}
        onClick={() => setActiveMainView(selected ? null : v.id)}
      >
        <Icon size={18} aria-hidden />
      </button>
    );
  };

  return (
    <div className="mp-activitybar" role="toolbar" aria-orientation="vertical" aria-label="Activity bar">
      <div className="mp-activitybar__panels">
        {topPanels.map(renderPanelButton)}
        {mainViews.map(renderMainViewButton)}
      </div>

      <div className="mp-activitybar__actions">
        <button
          type="button"
          className="mp-activitybar__btn"
          aria-label="New document"
          title="New document"
          onClick={onNew}
        >
          <FilePlus size={18} aria-hidden />
        </button>
        <button
          type="button"
          className="mp-activitybar__btn"
          aria-label="Open file"
          title="Open file"
          onClick={onOpenFile}
        >
          <FileUp size={18} aria-hidden />
        </button>
        <button
          type="button"
          className="mp-activitybar__btn"
          aria-label="Open folder"
          title="Open folder"
          onClick={onOpenFolder}
        >
          <FolderOpen size={18} aria-hidden />
        </button>
      </div>

      {bottomPanels.length > 0 && (
        <>
          <div className="mp-activitybar__sep" role="separator" aria-orientation="horizontal" />
          <div className="mp-activitybar__panels mp-activitybar__panels--bottom">
            {bottomPanels.map(renderPanelButton)}
          </div>
        </>
      )}

      <button
        type="button"
        className="mp-activitybar__btn mp-activitybar__toggle"
        aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
        title={open ? 'Collapse sidebar' : 'Expand sidebar'}
        onClick={() => toggleOpen()}
      >
        {open ? <PanelLeftClose size={18} aria-hidden /> : <PanelLeftOpen size={18} aria-hidden />}
      </button>
    </div>
  );
}
